import { sortBy } from 'lodash-es';
import moment from 'moment';
import {
  RemoteServiceDiagnosisMetadata,
  RemoteServiceDiagnosisResponse,
} from '../../../projects/client';
import {
  AnomalyLevel,
  RemoteServiceDiagnosisEntry,
  RemoteServiceDiagnosisTag,
} from './remote-service.model';

export namespace RemoteServiceMapper {
  export function normalizeDiagnosisData(
    diagnosis: RemoteServiceDiagnosisResponse,
    config: {
      isRsa: boolean;
      isMaintainer: boolean;
    }
  ): RemoteServiceDiagnosisEntry[] {
    // ###################################################################
    // 1. Extend data for additional helper properties
    const extendedDiagnosisData = diagnosis.data.data.map((entry, index) => ({
      ...entry,
      diagnosis_anomaly: entry.diagnosis_anomaly,
      selected: false,
      index,
      tags: [],
      diagnosis_id_display: formatDiagnosisId(entry.diagnosis_id),
    }));

    // ###################################################################
    // 2. We are getting whole list of anomalies diagnosis but we only want
    // to display anomaly of priority 1 from the first encountered day
    const mostRecentDiagnosisDate = moment(extendedDiagnosisData[0]?.diagnosis_date)
      .startOf('day')
      .toDate()
      .valueOf();

    const allowedDiagnosisAnomalyLevels = [
      AnomalyLevel.RootCause,
      AnomalyLevel.Information,
      AnomalyLevel.Anomaly,
    ];
    let mostRecentData: RemoteServiceDiagnosisEntry[] = extendedDiagnosisData
      .filter((entry) => moment(entry.diagnosis_date).toDate().valueOf() >= mostRecentDiagnosisDate)
      .filter((entry) => entry.diagnosis_text.length)
      .filter((entry) =>
        allowedDiagnosisAnomalyLevels.includes(entry.diagnosis_anomaly as AnomalyLevel)
      );
    // ###################################################################
    // 3. Filter to keep only the latest diagnosis by diagnosis_id
    const diagnosisMap = new Map<string, RemoteServiceDiagnosisEntry>();

    mostRecentData.forEach((entry) => {
      const existing = diagnosisMap.get(entry.diagnosis_id);
      const currentDate = moment(entry.diagnosis_date).valueOf();

      if (!existing || currentDate > moment(existing.diagnosis_date).valueOf()) {
        diagnosisMap.set(entry.diagnosis_id, entry);
      }
    });

    mostRecentData = Array.from(diagnosisMap.values());

    // ###################################################################
    // 4. Filter out diagnosis depending on user context and metadata
    const visibilityFilteredData = mostRecentData.filter((entry: RemoteServiceDiagnosisEntry) => {
      const audienceMetadata = entry?.meta_information?.audience;

      const noAudienceOptions = !audienceMetadata || audienceMetadata?.length === 0;

      if (noAudienceOptions) {
        return true;
      }

      // Only Internal Option - only for Rsa
      const isOnlyOneAudienceOption = audienceMetadata?.length === 1;
      const isInternalOption = audienceMetadata?.includes(
        RemoteServiceDiagnosisMetadata.AudienceOptions.Internal
      );
      const isCase1 = isOnlyOneAudienceOption && isInternalOption && !config.isRsa;

      if (isCase1) {
        return false;
      }

      // Customer Option - only for Rsa and Maintainer
      const isCustomerOption = audienceMetadata?.includes(
        RemoteServiceDiagnosisMetadata.AudienceOptions.Customer
      );
      const isCase2 = isCustomerOption && !(config.isRsa || config.isMaintainer);

      if (isCase2) {
        return false;
      }

      return true;
    });

    // ###################################################################
    // 5. Apply tags depending on metadata
    visibilityFilteredData.forEach((entry: RemoteServiceDiagnosisEntry) => {
      const audienceMetadata = entry?.meta_information?.audience;
      const releaseStageMetadata = entry?.meta_information?.release_stage;

      // Internal Rsa Tag
      const isOnlyOneAudienceOption = audienceMetadata?.length === 1;
      const isInternalOption = audienceMetadata?.includes(
        RemoteServiceDiagnosisMetadata.AudienceOptions.Internal
      );
      const isInternalRsaTag = isOnlyOneAudienceOption && isInternalOption && config.isRsa;

      if (isInternalRsaTag) {
        entry.tags.push(RemoteServiceDiagnosisTag.InternalRsa);
      }

      // Beta Tag
      const isBetaStage =
        releaseStageMetadata === RemoteServiceDiagnosisMetadata.ReleaseStageOptions.Beta;

      if (isBetaStage) {
        entry.tags.push(RemoteServiceDiagnosisTag.Beta);
      }

      // Gold Error Tag
      const isGoldErrorStage =
        releaseStageMetadata === RemoteServiceDiagnosisMetadata.ReleaseStageOptions.GoldError;

      if (isGoldErrorStage) {
        entry.tags.push(RemoteServiceDiagnosisTag.GoldError);
      }
    });

    // ###################################################################
    // 6. Sort data alphabeticaly
    const finalData = sortBy(visibilityFilteredData, ['diagnosis_group', 'diagnosis_name']);

    return finalData;
  }

  /**
   * Organizes a flat list of diagnosis entries into a hierarchical tree structure
   * and returns nodes in DFS order with sorted children.
   */
  export function sortDiagnosisTree(
    entries: RemoteServiceDiagnosisEntry[]
  ): RemoteServiceDiagnosisEntry[] {
    const diagnosisById = new Map<string, RemoteServiceDiagnosisEntry>();
    const childrenMap = new Map<string, RemoteServiceDiagnosisEntry[]>();

    entries.forEach((entry) => {
      diagnosisById.set(entry.diagnosis_id, entry);
    });

    const roots: RemoteServiceDiagnosisEntry[] = [];

    entries.forEach((entry) => {
      const parentId = entry?.meta_information?.parent_diagnosis;

      if (parentId && parentId !== entry.diagnosis_id && diagnosisById.has(parentId)) {
        if (!childrenMap.has(parentId)) {
          childrenMap.set(parentId, []);
        }

        const children = childrenMap.get(parentId);
        if (children) {
          children.push(entry);
        }
      } else {
        roots.push(entry);
      }
    });

    const result: RemoteServiceDiagnosisEntry[] = [];
    const visited = new Set<string>();

    roots.forEach((entry) => dfsDiagnosisTree(entry, childrenMap, visited, result));

    return result;
  }

  /**
   * Performs depth-first traversal of diagnosis tree with these rules:
   * - Visits parent before children (pre-order DFS)
   * - Sorts children alphabetically before traversal
   * - Prevents infinite loops via visited tracking
   *
   * @param entry Current node to process
   * @param childrenMap Pre-built parent-child relationships map
   * @param visited Set to track visited nodes (prevents cycles)
   * @param result Accumulator for ordered nodes
   */
  export function dfsDiagnosisTree(
    entry: RemoteServiceDiagnosisEntry,
    childrenMap: Map<string, RemoteServiceDiagnosisEntry[]>,
    visited: Set<string>,
    result: RemoteServiceDiagnosisEntry[]
  ): void {
    if (visited.has(entry.diagnosis_id)) {
      return;
    }
    visited.add(entry.diagnosis_id);
    result.push(entry);

    const children = childrenMap.get(entry.diagnosis_id) || [];
    children.sort((a, b) => a.diagnosis_name.localeCompare(b.diagnosis_name));
    children.forEach((child) => dfsDiagnosisTree(child, childrenMap, visited, result));
  }

  /**
   * Format raw anomaly ID to an user friendly value
   * e.g. fdd_1 -> D.1
   *
   * @param diagnosis_id - raw diagnosis id like fdd_1
   */
  export function formatDiagnosisId(diagnosis_id: string): string {
    if (diagnosis_id) {
      return diagnosis_id.replace(/(fdd_|add_)/, 'D.');
    }
  }
}
